/**
 * Formatted fragments of an object's text (like Word): `props.spans` next to the plain `props.text`.
 * The spans' texts put together are exactly `props.text`; when they are not (an older client changed
 * the text alone), the spans are ignored and the text is shown plain. Plain text stays the source for
 * search, export of labels and everything that does not know about formatting.
 */
export interface TextSpan {
  t: string;
  /** bold, italic, underline, strike-through */
  b?: true;
  i?: true;
  u?: true;
  s?: true;
  /** text color, highlight (background) — #rrggbb or #rrggbbaa */
  c?: string;
  bg?: string;
  /** CSS font-family (a platform font stack) */
  f?: string;
  /** size relative to the object's font size (scales with the object) */
  k?: number;
}

export type SpanLook = Omit<TextSpan, 't'>;

const COLOR = /^#[0-9a-f]{6}([0-9a-f]{2})?$/i;
const FAMILY = /^[^;{}<>\\]{1,200}$/;
export const SPAN_SIZE_MIN = 0.4;
export const SPAN_SIZE_MAX = 4;

/** A clean look: only known, valid fields, no defaults. */
export function cleanLook(raw: unknown): SpanLook {
  const r = (raw ?? {}) as Record<string, unknown>;
  const out: SpanLook = {};
  if (r['b'] === true) out.b = true;
  if (r['i'] === true) out.i = true;
  if (r['u'] === true) out.u = true;
  if (r['s'] === true) out.s = true;
  if (typeof r['c'] === 'string' && COLOR.test(r['c'])) out.c = r['c'];
  if (typeof r['bg'] === 'string' && COLOR.test(r['bg'])) out.bg = r['bg'];
  if (typeof r['f'] === 'string' && FAMILY.test(r['f'])) out.f = r['f'];
  if (typeof r['k'] === 'number' && Number.isFinite(r['k']) && Math.abs(r['k'] - 1) > 1e-3) out.k = Math.min(SPAN_SIZE_MAX, Math.max(SPAN_SIZE_MIN, Math.round(r['k'] * 1000) / 1000));
  return out;
}

export function sameLook(a: SpanLook, b: SpanLook): boolean {
  return a.b === b.b && a.i === b.i && a.u === b.u && a.s === b.s && a.c === b.c && a.bg === b.bg && a.f === b.f && (a.k ?? 1) === (b.k ?? 1);
}

export function isPlainLook(l: SpanLook): boolean {
  return !l.b && !l.i && !l.u && !l.s && !l.c && !l.bg && !l.f && (l.k ?? 1) === 1;
}

/** Merge neighbours that look the same, drop empty spans. */
export function normalizeSpans(spans: readonly TextSpan[]): TextSpan[] {
  const out: TextSpan[] = [];
  for (const s of spans) {
    if (!s.t) continue;
    const look = cleanLook(s);
    const last = out[out.length - 1];
    if (last && sameLook(last, look)) last.t += s.t;
    else out.push({ t: s.t, ...look });
  }
  return out;
}

export function spansText(spans: readonly TextSpan[]): string {
  let s = '';
  for (const x of spans) s += x.t;
  return s;
}

export function hasFormatting(spans: readonly TextSpan[]): boolean {
  return spans.some((s) => !isPlainLook(s));
}

/** Valid spans of an object's text, or null (no formatting, damaged or out of date). */
export function readSpans(text: string, raw: unknown): TextSpan[] | null {
  if (!Array.isArray(raw) || !raw.length || raw.length > 20000) return null;
  const spans: TextSpan[] = [];
  for (const r of raw) {
    if (!r || typeof r !== 'object' || typeof (r as { t?: unknown }).t !== 'string') return null;
    spans.push({ t: (r as { t: string }).t, ...cleanLook(r) });
  }
  if (spansText(spans) !== text) return null;
  const norm = normalizeSpans(spans);
  return hasFormatting(norm) ? norm : null;
}

/** Font stacks used by the spans. */
export function spanFamilies(spans: readonly TextSpan[]): string[] {
  return [...new Set(spans.map((s) => s.f).filter((f): f is string => !!f))];
}
